import { ref } from "vue";

import { debounce } from "../helpers";

export const useEntityStore = ({
    getEntities,
    getEntity,
    addEntity,
    deleteEntity,
    deleteEntities,
    editEntity,
    exportEntities,
    getRights,
    editRights,
}) => {
    const listData = ref([]);
    const singleData = ref({});
    const isManyLoading = ref(false);
    const isOneLoading = ref(false);
    const meta = ref({
        total: 0,
        page: 1,
    });
    const permissions = ref({});
    const prevPaginationParams = ref({});
    const prevFilters = ref({});
    const lastRequestId = ref(0);
    const isRequestInProgress = ref(false);
    const pendingRequest = ref(null);

    // Функция для выполнения запроса с проверкой актуальности
    const executeRequest = async (
        requestId,
        paginationParams,
        filters,
        options
    ) => {
        try {
            // Если этот запрос уже не актуален, прерываем выполнение
            if (requestId < lastRequestId.value) {
                return;
            }

            isManyLoading.value = true;

            const fetchResponse = await getEntities(
                paginationParams,
                filters,
                options
            );

            // Проверяем актуальность ответа перед обновлением данных
            if (requestId === lastRequestId.value) {
                listData.value = fetchResponse.data;
                meta.value = fetchResponse.meta;
                permissions.value = fetchResponse.permissions;

                prevPaginationParams.value = paginationParams;
                prevFilters.value = filters;
            }
        } finally {
            isRequestInProgress.value = false;

            if (pendingRequest.value) {
                const { requestId, paginationParams, filters, options } =
                    pendingRequest.value;

                pendingRequest.value = null;

                executeRequest(requestId, paginationParams, filters, options);
            } else {
                isManyLoading.value = false;
            }
        }
    };

    const fetchMany = debounce(
        async (
            paginationParams = prevPaginationParams.value,
            filters = prevFilters.value,
            options = {}
        ) => {
            // Увеличиваем счетчик запросов
            lastRequestId.value++;
            const currentRequestId = lastRequestId.value;

            // Если запрос уже выполняется, запоминаем новый как отложенный
            if (isRequestInProgress.value) {
                pendingRequest.value = {
                    requestId: currentRequestId,
                    paginationParams,
                    filters,
                    options,
                };

                return;
            }

            // Помечаем, что запрос в процессе выполнения
            isRequestInProgress.value = true;

            // Выполняем запрос
            executeRequest(
                currentRequestId,
                paginationParams,
                filters,
                options
            );
        },
        300
    );

    async function getOne(id) {
        try {
            isOneLoading.value = true;

            singleData.value = await getEntity(id);

            return singleData.value;
        } finally {
            isOneLoading.value = false;
        }
    }

    async function editOne(id, data) {
        try {
            isOneLoading.value = true;

            singleData.value = await editEntity(id, data);

            return singleData.value;
        } finally {
            isOneLoading.value = false;
        }
    }

    async function addOne(data) {
        try {
            isOneLoading.value = true;

            singleData.value = await addEntity(data);

            return singleData.value;
        } finally {
            isOneLoading.value = false;
        }
    }

    async function fetchRights(id) {
        try {
            isOneLoading.value = true;

            return await getRights(id);
        } finally {
            isOneLoading.value = false;
        }
    }

    async function updateRights(id, rightsData) {
        try {
            isOneLoading.value = true;

            return await editRights(id, rightsData);
        } finally {
            isOneLoading.value = false;
        }
    }

    async function deleteOne(id) {
        try {
            isManyLoading.value = true;
            isOneLoading.value = true;

            await deleteEntity(id);
        } finally {
            isManyLoading.value = false;
            isOneLoading.value = false;
        }
    }

    async function deleteMany(ids) {
        try {
            isManyLoading.value = true;
            isOneLoading.value = true;

            await deleteEntities(ids);
        } finally {
            isManyLoading.value = false;
            isOneLoading.value = false;
        }
    }

    async function exportMany(paginationParams, filters) {
        try {
            isManyLoading.value = true;

            await exportEntities(paginationParams, filters);
        } finally {
            isManyLoading.value = false;
        }
    }

    function setManyLoading(value) {
        isManyLoading.value = value;
    }

    // Функция для сброса состояния при размонтировании компонента
    const cleanup = () => {
        // Увеличиваем ID запроса, чтобы любые незавершенные запросы были проигнорированы
        lastRequestId.value++;
        isRequestInProgress.value = false;
        pendingRequest.value = null;
    };

    return {
        listData,
        singleData,
        isManyLoading,
        isOneLoading,
        meta,
        permissions,
        fetchMany,
        deleteOne,
        deleteMany,
        getOne,
        editOne,
        addOne,
        fetchRights,
        updateRights,
        exportMany,
        setManyLoading,
        cleanup,
    };
};
